<template>
  <div class="report-page">
    <div class="report-header">
      <el-button size="small" @click="goBack">← 返回列表</el-button>
      <h2 class="report-title">测试报表</h2>
      <span class="report-sub">共选中 {{ tests.length }} 个测试</span>
    </div>

    <div v-if="loading" class="report-loading">
      正在加载测试数据…
    </div>

    <template v-else>
      <!-- 概览统计卡片 -->
      <div class="overview-cards">
        <div class="stat-card">
          <div class="stat-value">{{ overview.totalTests }}</div>
          <div class="stat-label">已选测试</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ overview.finishedTests }}</div>
          <div class="stat-label">已完成</div>
        </div>
        <div class="stat-card">
          <div class="stat-value">{{ overview.totalWords }}</div>
          <div class="stat-label">总单词数</div>
        </div>
        <div class="stat-card stat-wrong">
          <div class="stat-value">{{ overview.totalWrong }}</div>
          <div class="stat-label">错误单词数</div>
        </div>
        <div class="stat-card stat-acc">
          <div class="stat-value">{{ overview.avgAccuracy }}%</div>
          <div class="stat-label">平均正确率</div>
        </div>
      </div>

      <div v-if="finishedTests.length === 0" class="report-empty">
        选中的测试均未完成，暂无统计数据。请选择已完成的测试。
      </div>

      <template v-else>
        <!-- 按来源分布 -->
        <div class="chart-card">
          <h3 class="chart-title">按来源（origin）分布</h3>
          <div class="bar-chart">
            <div
              class="bar-row"
              v-for="item in originStats"
              :key="item.origin"
            >
              <div class="bar-label" :title="item.origin">{{ item.origin }}</div>
              <div class="bar-track">
                <div
                  class="bar-fill"
                  :style="{ width: barWidth(item.count, originMax) + '%' }"
                ></div>
              </div>
              <div class="bar-value">
                {{ item.count }} 个 · 正确率 {{ item.accuracy }}%
              </div>
            </div>
          </div>
        </div>

        <!-- 按模式分布 -->
        <div class="chart-card">
          <h3 class="chart-title">按模式（type）分布</h3>
          <div class="bar-chart">
            <div class="bar-row" v-for="item in typeStats" :key="item.type">
              <div class="bar-label">{{ typeName(item.type) }}</div>
              <div class="bar-track">
                <div
                  class="bar-fill bar-fill-type"
                  :style="{ width: barWidth(item.count, typeMax) + '%' }"
                ></div>
              </div>
              <div class="bar-value">
                {{ item.count }} 个 · 正确率 {{ item.accuracy }}%
              </div>
            </div>
          </div>
        </div>

        <!-- 正确率趋势折线图 -->
        <div class="chart-card">
          <h3 class="chart-title">正确率趋势（按完成时间）</h3>
          <div class="line-chart">
            <svg :width="lineWidth" :height="lineHeight" :viewBox="'0 0 ' + lineWidth + ' ' + lineHeight">
              <!-- 水平网格线 + y 轴刻度 -->
              <g v-for="tick in yTicks" :key="tick">
                <line
                  :x1="pad.left"
                  :y1="yFor(tick)"
                  :x2="lineWidth - pad.right"
                  :y2="yFor(tick)"
                  class="grid-line"
                />
                <text :x="pad.left - 8" :y="yFor(tick) + 4" class="axis-text" text-anchor="end">
                  {{ tick }}%
                </text>
              </g>
              <!-- 折线 -->
              <polyline
                v-if="timeTrend.length > 1"
                :points="linePoints"
                class="trend-line"
              />
              <!-- 数据点 + 数值标签 + x 轴标签 -->
              <g v-for="(d, i) in timeTrend" :key="i">
                <circle
                  :cx="xFor(i)"
                  :cy="yFor(d.accuracy)"
                  r="4.5"
                  class="trend-dot"
                />
                <text
                  :x="xFor(i)"
                  :y="yFor(d.accuracy) - 10"
                  class="dot-value"
                  text-anchor="middle"
                >
                  {{ d.accuracy }}%
                </text>
                <text
                  :x="xFor(i)"
                  :y="lineHeight - 10"
                  class="axis-text x-label"
                  text-anchor="middle"
                >
                  {{ d.dateLabel }}
                </text>
              </g>
            </svg>
            <div class="line-legend">每个点代表一次已完成测试，纵轴为正确率（%），横轴按完成时间排序</div>
          </div>
        </div>

        <!-- 错误单词清单 -->
        <div class="chart-card">
          <div class="collapse-header">
            <h3 class="chart-title">错误单词清单（按测试）</h3>
            <div class="collapse-actions">
              <el-button size="small" @click="expandAll">全部展开</el-button>
              <el-button size="small" @click="collapseAll">全部收缩</el-button>
            </div>
          </div>
          <el-collapse v-model="activeCollapse">
            <el-collapse-item
              v-for="t in finishedTests"
              :key="t.id"
              :name="String(t.id)"
            >
              <template #title>
                <div class="collapse-title">
                  <span class="collapse-name">{{ t.name }}</span>
                  <el-tag size="small" :type="t.wrongCount > 0 ? 'danger' : 'success'" effect="plain">
                    错误 {{ t.wrongCount }} / {{ t.total }} · 正确率 {{ t.accuracy }}%
                  </el-tag>
                </div>
              </template>
              <el-table
                v-if="t.wrongAnswers.length"
                :data="t.wrongAnswers"
                size="small"
                border
                max-height="360"
              >
                <el-table-column type="index" width="50" />
                <el-table-column prop="englishText" label="单词" min-width="120" />
                <el-table-column prop="chineseText" label="释义" min-width="200" show-overflow-tooltip />
                <el-table-column prop="answer" label="学生作答" min-width="160" show-overflow-tooltip />
              </el-table>
              <div v-else class="no-wrong">本次测试没有错误单词 🎉</div>
            </el-collapse-item>
          </el-collapse>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
import pinyin from "pinyin";

export default {
  name: "TestReport",

  data() {
    return {
      tests: [],
      loading: true,
      activeCollapse: [],
      testDetails: [],
      overview: {
        totalTests: 0,
        finishedTests: 0,
        totalWords: 0,
        totalWrong: 0,
        avgAccuracy: 0,
      },
      // 图表尺寸
      lineWidth: 760,
      lineHeight: 280,
      pad: { left: 56, right: 24, top: 24, bottom: 44 },
      yTicks: [0, 25, 50, 75, 100],
    };
  },

  computed: {
    finishedTests() {
      return this.testDetails.filter(
        (t) => t.detail && t.total > 0
      );
    },
    originStats() {
      const map = {};
      this.finishedTests.forEach((t) => {
        const key = (t.origin || "未标注").trim() || "未标注";
        if (!map[key]) {
          map[key] = { origin: key, count: 0, correct: 0, total: 0 };
        }
        map[key].count += 1;
        map[key].correct += t.correct;
        map[key].total += t.total;
      });
      return Object.values(map)
        .map((o) => ({
          ...o,
          accuracy: o.total ? Math.round((o.correct / o.total) * 10000) / 100 : 0,
        }))
        .sort((a, b) => b.count - a.count);
    },
    originMax() {
      return Math.max(1, ...this.originStats.map((o) => o.count));
    },
    typeStats() {
      const map = {};
      this.finishedTests.forEach((t) => {
        const key = t.type;
        if (!map[key]) {
          map[key] = { type: key, count: 0, correct: 0, total: 0 };
        }
        map[key].count += 1;
        map[key].correct += t.correct;
        map[key].total += t.total;
      });
      return Object.values(map)
        .map((o) => ({
          ...o,
          accuracy: o.total ? Math.round((o.correct / o.total) * 10000) / 100 : 0,
        }))
        .sort((a, b) => a.type - b.type);
    },
    typeMax() {
      return Math.max(1, ...this.typeStats.map((o) => o.count));
    },
    timeTrend() {
      const sorted = [...this.finishedTests].sort((a, b) => {
        return this.dateOf(a) - this.dateOf(b);
      });
      return sorted.map((t) => ({
        accuracy: t.accuracy,
        dateLabel: this.dateLabel(this.dateOf(t)),
      }));
    },
    linePoints() {
      return this.timeTrend
        .map((d, i) => `${this.xFor(i)},${this.yFor(d.accuracy)}`)
        .join(" ");
    },
  },

  beforeMount() {
    try {
      const qs = window.atob(this.$route.query.encodedString);
      const decoded = JSON.parse(decodeURIComponent(qs));
      this.tests = (decoded && decoded.tests) || [];
    } catch (e) {
      this.tests = [];
    }
  },

  mounted() {
    this.loadReport();
  },

  methods: {
    goBack() {
      this.$router.push("/studenttest");
    },

    typeName(type) {
      switch (type) {
        case 1:
          return "听写英文";
        case 2:
          return "听写中英";
        case 3:
          return "听英写中";
        case 4:
          return "看英写中";
        case 5:
          return "语音测试";
        case 6:
          return "看中写英";
        case 7:
          return "看英说英";
        default:
          return "类型 " + type;
      }
    },

    stripHtml(s) {
      if (s == null) return "";
      return String(s)
        .replace(/<[^>]+>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },

    // 判断某条答案是否正确（复刻结果页 NewTestItemResult 的口径）
    judgeAnswer(a, type) {
      // 语音测试 / 看英说英：优先用后端 isCorrect，其次 AI 批改 ds
      if (type === 5 || type === 7) {
        if (typeof a.isCorrect === "boolean") return a.isCorrect;
        return String(a.ds) === "1";
      }
      const needEn = [1, 2, 6].includes(type);
      const needCn = [2, 3, 4].includes(type);
      let enOk = true;
      let cnOk = true;
      if (needEn) enOk = this.judgeEn(a);
      if (needCn) cnOk = this.judgeCn(a);
      return enOk && cnOk;
    },

    // 英文拼写判断（大小写不敏感、trim）
    judgeEn(a) {
      const ans = a.englishStudentAnswer;
      if (!ans || ans === "NOT Answered") return false;
      return (
        String(a.englishText || "").trim().toLowerCase() ===
        String(ans).trim().toLowerCase()
      );
    },

    // 中文释义判断（去标点匹配标准答案 + 拼音兜底）
    judgeCn(a) {
      const ans = a.chineseStudentAnswer;
      if (!ans || ans === "NOT Answered") return false;

      // 标准答案集合：后端 correctedAnswers + 从 chineseText 提取的释义
      let corrected = (a.correctedAnswers || [])
        .map((s) => s && s.trim())
        .filter(Boolean);
      const cnTextArray = this.generateOriginalCnTextArray(a.chineseText);
      corrected = corrected.concat(cnTextArray);

      const stu = String(ans)
        .trim()
        .replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, "");
      if (!stu) return false;

      const normalized = corrected.map((s) =>
        String(s).replace(/[^\u4e00-\u9fa5a-zA-Z0-9]/g, "")
      );
      if (normalized.includes(stu)) return true;

      // 拼音兜底
      const pinyinStu = pinyin(stu, { style: pinyin.STYLE_NORMAL }).join("");
      const pinyinCorrected = normalized.map((s) =>
        pinyin(s, { style: pinyin.STYLE_NORMAL }).join("")
      );
      return pinyinCorrected.includes(pinyinStu);
    },

    // 从 chineseText 提取中文释义词组（复刻结果页 generateOriginalCnTextArray）
    generateOriginalCnTextArray(text) {
      if (!text) return [];
      let raw = [];
      if (text.includes("释义：")) {
        raw = this.retrieveCnMeaning(text);
      } else {
        raw.push(text);
      }
      const result = [];
      for (let j = 0; j < raw.length; j++) {
        const cnTextArray = raw[j].split(/[,，.。、;；:：?？!！ ]/);
        for (let i = 0; i < cnTextArray.length; i++) {
          if (this.isChinese(cnTextArray[i])) {
            result.push(cnTextArray[i].replace("…", ""));
            if (cnTextArray[i].includes("的")) {
              result.push(cnTextArray[i].replace("…", "").replace("的", ""));
            }
            if (cnTextArray[i].includes("地")) {
              result.push(cnTextArray[i].replace("…", "").replace("地", ""));
            }
          }
        }
      }
      return result;
    },

    // 提取所有「释义：」之后、标签之前的内容
    retrieveCnMeaning(text) {
      const substr = "释义：";
      const indices = [];
      for (const match of text.matchAll(new RegExp(substr, "g"))) {
        indices.push(match.index + substr.length);
      }
      const result = [];
      for (let i = 0; i < indices.length; i++) {
        const start = indices[i];
        const end = text.indexOf("<", start);
        result.push(text.substring(start, end === -1 ? text.length : end));
      }
      return result;
    },

    isChinese(text) {
      if (!text) return false;
      const re =
        /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/g;
      return re.test(text);
    },

    dateOf(t) {
      const raw = t.endDate || (t.detail && t.detail.endDate) || t.createdDate;
      const d = new Date(raw);
      return isNaN(d.getTime()) ? 0 : d.getTime();
    },

    dateLabel(ts) {
      if (!ts) return "未知";
      const d = new Date(ts);
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${m}-${day}`;
    },

    loadReport() {
      this.loading = true;
      const tasks = this.tests.map((t) =>
        this.$store
          .dispatch("StudentTest/getTestById", {
            id: t.id,
            userId: t.studentId || localStorage.getItem("targetId"),
            includeAudio: false,
          })
          .then((res) => ({ ...t, detail: res.data }))
          .catch((err) => ({ ...t, error: (err && err.message) || String(err) }))
      );
      Promise.all(tasks)
        .then((results) => {
          this.process(results);
          this.loading = false;
        })
        .catch((err) => {
          this.loading = false;
          this.$message("加载报表数据失败：" + String(err));
        });
    },

    process(results) {
      this.testDetails = results.map((r) => {
        const answers =
          r.detail && Array.isArray(r.detail.studentAnswers)
            ? r.detail.studentAnswers
            : [];
        const type = r.type != null ? r.type : r.detail && r.detail.type;
        const total = answers.length;
        const correct = answers.filter((a) => this.judgeAnswer(a, type))
          .length;
        const wrong = total - correct;
        const accuracy = total
          ? Math.round((correct / total) * 10000) / 100
          : 0;
        const wrongAnswers = answers
          .filter((a) => !this.judgeAnswer(a, type))
          .map((a) => ({
            englishText: a.englishText || "",
            chineseText: this.stripHtml(a.chineseText),
            answer:
              a.englishStudentAnswer ||
              a.chineseStudentAnswer ||
              a.textConvertedByAudio ||
              "",
          }));
        return {
          ...r,
          answers,
          total,
          correct,
          wrong,
          accuracy,
          wrongAnswers,
        };
      });

      // 概览
      const totalWords = this.testDetails.reduce((s, t) => s + t.total, 0);
      const totalWrong = this.testDetails.reduce((s, t) => s + t.wrong, 0);
      const totalCorrect = this.testDetails.reduce((s, t) => s + t.correct, 0);
      this.overview = {
        totalTests: this.testDetails.length,
        finishedTests: this.finishedTests.length,
        totalWords,
        totalWrong,
        avgAccuracy: totalWords
          ? Math.round((totalCorrect / totalWords) * 10000) / 100
          : 0,
      };

      // 默认全部展开错误单词清单
      this.activeCollapse = this.finishedTests.map((t) => String(t.id));
    },

    expandAll() {
      this.activeCollapse = this.finishedTests.map((t) => String(t.id));
    },

    collapseAll() {
      this.activeCollapse = [];
    },

    // 柱状图宽度百分比
    barWidth(value, max) {
      return Math.round((value / max) * 100);
    },

    // 折线图坐标
    xFor(i) {
      const n = this.timeTrend.length;
      const innerW = this.lineWidth - this.pad.left - this.pad.right;
      if (n <= 1) return this.pad.left + innerW / 2;
      return this.pad.left + (i * innerW) / (n - 1);
    },
    yFor(accuracy) {
      const innerH = this.lineHeight - this.pad.top - this.pad.bottom;
      return (
        this.pad.top + ((100 - accuracy) / 100) * innerH
      );
    },
  },
};
</script>

<style scoped>
.report-page {
  padding: 16px 5%;
  background: #f7f8fa;
  min-height: 100vh;
}
.report-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
}
.report-title {
  margin: 0;
  font-size: 20px;
  color: #303133;
}
.report-sub {
  color: #909399;
  font-size: 13px;
}
.report-loading {
  padding: 60px 0;
  text-align: center;
  color: #909399;
  font-size: 14px;
}
.report-empty {
  padding: 60px 0;
  text-align: center;
  color: #909399;
}

/* 概览卡片 */
.overview-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.stat-card {
  flex: 1;
  min-width: 120px;
  background: #fff;
  border-radius: 8px;
  padding: 18px 16px;
  text-align: center;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  line-height: 1.2;
}
.stat-wrong .stat-value {
  color: #f56c6c;
}
.stat-acc .stat-value {
  color: #409eff;
}
.stat-label {
  margin-top: 6px;
  font-size: 13px;
  color: #909399;
}

/* 图表卡片 */
.chart-card {
  background: #fff;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}
.chart-title {
  margin: 0 0 16px;
  font-size: 16px;
  color: #303133;
}

/* 横向柱状图 */
.bar-chart {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.bar-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.bar-label {
  flex: 0 0 200px;
  max-width: 200px;
  text-align: right;
  font-size: 13px;
  color: #606266;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.bar-track {
  flex: 1;
  height: 18px;
  background: #f0f2f5;
  border-radius: 9px;
  overflow: hidden;
}
.bar-fill {
  height: 100%;
  background: #409eff;
  border-radius: 9px;
  transition: width 0.4s ease;
}
.bar-fill-type {
  background: #67c23a;
}
.bar-value {
  flex: 0 0 auto;
  font-size: 13px;
  color: #606266;
  white-space: nowrap;
}

/* 折线图 */
.line-chart {
  overflow-x: auto;
}
.grid-line {
  stroke: #e4e7ed;
  stroke-width: 1;
}
.axis-text {
  font-size: 11px;
  fill: #909399;
}
.x-label {
  font-size: 11px;
}
.trend-line {
  fill: none;
  stroke: #409eff;
  stroke-width: 2.5;
}
.trend-dot {
  fill: #409eff;
  stroke: #fff;
  stroke-width: 1.5;
}
.dot-value {
  font-size: 11px;
  fill: #303133;
  font-weight: 600;
}
.line-legend {
  margin-top: 6px;
  font-size: 12px;
  color: #909399;
}

/* 错误单词 */
.collapse-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
}
.collapse-actions {
  display: flex;
  gap: 8px;
}
.collapse-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.collapse-name {
  font-weight: 600;
  color: #303133;
}
.no-wrong {
  padding: 16px;
  text-align: center;
  color: #67c23a;
  font-size: 14px;
}
</style>
